import Enumerable from "linq";
import { AuthenticatedService } from "../../../../base/infrastructure";
import { TextAlignKinds } from "../../../../base/domain/drawing";
import {
  InputMethodKinds,
  PatientProfileCheckKinds,
  TranscribeTargetKinds,
  HisInformationItemDefaultSelectKinds,
} from "../../../../documaker/domain/format/objects";
import {
  IFormatSpecificationRepository,
  IFormatSpecification,
  PaperUtility,
  IUserDefinitionSpecification,
  IExternalScriptSpecification,
  IBarcodeInformationSpecification,
} from "../../../domain/editableFormat";
import { DomainRegistry } from "../../../domain";

export class FormatSpecificationRepository extends AuthenticatedService implements IFormatSpecificationRepository {
  constructor(getToken: () => string) {
    super(getToken);
  }

  private async getUserDefinitions(): Promise<IUserDefinitionSpecification[]> {
    const result = await this.get<{ userDefinitions: IUserDefinitionSpecification[] }>("api/format-editor/user-definitions/get");
    return result.data.userDefinitions;
  }

  private async getExternalScripts(): Promise<IExternalScriptSpecification[]> {
    const result = await this.get<{ externalScripts: IExternalScriptSpecification[] }>("api/format-editor/external-scripts/get");
    return result.data.externalScripts;
  }

  private async getBarcodeInformations(): Promise<IBarcodeInformationSpecification[]> {
    const result = await this.get<{ barcodeInformations: IBarcodeInformationSpecification[] }>("api/format-editor/barcode-informations/get");
    return result.data.barcodeInformations;
  }

  async getSpecification(version: string): Promise<IFormatSpecification> {
    if (!this._specifications[version]) {
      this._specifications[version] = await this._getSpecification(version);
    }
    return this._specifications[version];
  }

  async getFontFamilyNames(): Promise<string[]> {
    const result = await this.get<{ folders: string[] }>("api/format-editor/font-names/get");
    return result.data.folders;
  }

  private _specifications: { [key: string]: IFormatSpecification; } = {};

  private allAlignmentKinds: TextAlignKinds[] = [
    TextAlignKinds.TopLeft,
    TextAlignKinds.TopCenter,
    TextAlignKinds.TopRight,
    TextAlignKinds.MiddleLeft,
    TextAlignKinds.MiddleCenter,
    TextAlignKinds.MiddleRight,
    TextAlignKinds.BottomLeft,
    TextAlignKinds.BottomCenter,
    TextAlignKinds.BottomRight,
  ];

  private alignmentKindsFixedMiddle: TextAlignKinds[] = [
    TextAlignKinds.MiddleLeft,
    TextAlignKinds.MiddleCenter,
    TextAlignKinds.MiddleRight,
  ];

  private async _getSpecification(version: string): Promise<IFormatSpecification> {
    const hisRepository = DomainRegistry.his.getHisInformationRepository();
    const designatedDoctorRepository = DomainRegistry.user.getDesignatedDoctorRepository();
    const results = await Promise.all([
      hisRepository.getHisInformationSettings(),
      this.getUserDefinitions(),
      this.getExternalScripts(),
      this.getFontFamilyNames(),
      this.getBarcodeInformations(),
      designatedDoctorRepository.getAllEntities()
    ]);
    return {
      version: version,
      papers: [
        PaperUtility.A4,
        PaperUtility.A3,
        PaperUtility.A5,
        PaperUtility.B4,
        PaperUtility.B5,
      ],
      objects: {
        barcode: {
          qrCode: {
            cellSizes: [
              2,  // 小.
              3,  // 中.
              4,  // 大.
            ],
            versions: Enumerable.range(1, 20).toArray(),
          },
          barcodeInformations: results[4],
          cscanSets: [
            {
              name: "ドキュメントID",
              style: {
                kind: 1,
                separator: ",",
                qrCode: {
                  cellSize: 2,
                  version: 5,
                },
              },
              items:  [
                { isHisItem: false, value: "documaker"},
                { isHisItem: true, value: "DOCUMENT_ID"},
                { isHisItem: true, value: "REPORT_ID" },
                { isHisItem: true, value: "REPORT_VERSION_NUMBER" },
                { isHisItem: true, value: "LAYER_ID" },
                { isHisItem: true, value: "REPORT_TYPE_ID" },
                { isHisItem: true, value: "SRYKA_CODE" },
                { isHisItem: true, value: "USER_ID" },
                { isHisItem: true, value: "CREATE_DATE_TIME" }
              ],
            },
            {
              name: "オーダ連携ID",
              style: {
                kind: 1,
                separator: ",",
                qrCode: {
                  cellSize: 2,
                  version: 5,
                },
              },
              items:  [
                { isHisItem: true, value: "documaker"},
                { isHisItem: false, value: "オーダーID（C-Scan同意書連携）"},
                { isHisItem: true, value: "REPORT_ID" },
                { isHisItem: true, value: "REPORT_VERSION_NUMBER" },
                { isHisItem: true, value: "LAYER_ID" },
                { isHisItem: true, value: "REPORT_TYPE_ID" },
                { isHisItem: true, value: "SRYKA_CODE" },
                { isHisItem: true, value: "USER_ID" },
                { isHisItem: true, value: "CREATE_DATE_TIME" }
              ],
            },
            {
              name: "検査オーダ連携",
              style: {
                kind: 1,
                separator: ",",
                qrCode: {
                  cellSize: 2,
                  version: 5,
                },
              },
              items:  [
                { isHisItem: true, value: "documaker"},
                { isHisItem: false, value: "オーダーID（C-Scan同意書連携）"},
                { isHisItem: true, value: "REPORT_ID" },
                { isHisItem: true, value: "REPORT_VERSION_NUMBER" },
                { isHisItem: true, value: "LAYER_ID" },
                { isHisItem: true, value: "REPORT_TYPE_ID" },
                { isHisItem: true, value: "SRYKA_CODE" },
                { isHisItem: true, value: "USER_ID" },
                { isHisItem: true, value: "CREATE_DATE_TIME" },
                { isHisItem: true, value: "ORDER_NUMBER" }
              ],
            },
          ],
        },
        check: {
          hisInformations: Enumerable.from(results[0]).where(i => i.objectType === "CHECK").toArray(),
          patientProfileCheckKinds: [
            PatientProfileCheckKinds.None,
            PatientProfileCheckKinds.TrueOrFalse,
          ],
          isGroupScopeSelectable: true,
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
              TranscribeTargetKinds.Check,
              TranscribeTargetKinds.CircleSelect,
            ],
          },
        },
        circleSelect: {
          hisInformations: Enumerable.from(results[0]).where(i => i.objectType === "CIRCLESELECT").toArray(),
          patientProfileCheckKinds: [
            PatientProfileCheckKinds.None,
            PatientProfileCheckKinds.TrueOrFalse,
            PatientProfileCheckKinds.defaultChecked,
            PatientProfileCheckKinds.defaultCheckedOrEmpty,
          ],
          isGroupScopeSelectable: true,
          transcription: {
            targets: [
              TranscribeTargetKinds.Check,
              TranscribeTargetKinds.CircleSelect,
            ],
          },
      },
        date: {
          hisInformations: Enumerable.from(results[0]).where(i => (i.objectType === "DATE") || (i.objectType === "TIME")).toArray(),
          alignmentKinds: this.alignmentKindsFixedMiddle,
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
            ],
          },
          kinds: {
            date: {
              hisInformationItemDefaultSelectKinds: [
                HisInformationItemDefaultSelectKinds.Top,
                HisInformationItemDefaultSelectKinds.CreatedDate,
                HisInformationItemDefaultSelectKinds.Bottom,
              ],
            },
            time: {
              hisInformationItemDefaultSelectKinds: [
                HisInformationItemDefaultSelectKinds.Top,
                HisInformationItemDefaultSelectKinds.Bottom,
              ],
            },
          },
        },
        tag: {
          inputMethods: [
            InputMethodKinds.None,
            InputMethodKinds.HalfWidthOnly,
            InputMethodKinds.FullWidthOnly,
            InputMethodKinds.HalfWidthAlphabetOnly,
          ],
          alignmentKinds: this.allAlignmentKinds,
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
            ],
          },
        },
        text: {
          hisInformations: Enumerable.from(results[0]).where(i => (i.objectType === "TEXT") || (i.objectType === "TEXTAREA")).toArray(),
          inputMethods: [
            InputMethodKinds.None,
            InputMethodKinds.HalfWidthOnly,
            InputMethodKinds.FullWidthOnly,
            InputMethodKinds.HalfWidthAlphabetOnly,
          ],
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
            ],
          },
          hisInformationItemDefaultSelectKinds: [
            HisInformationItemDefaultSelectKinds.Top,
            HisInformationItemDefaultSelectKinds.Bottom,
          ],
          textObjects: {
            freeText: {
              alignmentKinds: this.allAlignmentKinds,
            },
            fixedText: {
              alignmentKinds: this.alignmentKindsFixedMiddle,
            },
            numberText: {
              alignmentKinds: this.allAlignmentKinds,
            },
            hisText: {
              alignmentKinds: this.alignmentKindsFixedMiddle,
            },
            userDefinedItem: {
              alignmentKinds: this.allAlignmentKinds,
            },
            designatedDoctorNumberText: {
              alignmentKinds: this.allAlignmentKinds,
            },
            rangeOfMotionText: {
              alignmentKinds: this.alignmentKindsFixedMiddle,
            }
          },
        },
        script: {
          inputMethods: [
            InputMethodKinds.None,
            InputMethodKinds.HalfWidthOnly,
            InputMethodKinds.FullWidthOnly,
          ],
          alignmentKinds: this.alignmentKindsFixedMiddle,
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
            ],
          },
        },
        jointext: {
          alignmentKinds: this.alignmentKindsFixedMiddle,
        },
        calculate: {
          alignmentKinds: this.alignmentKindsFixedMiddle,
        },
        combo: {
          alignmentKinds: this.alignmentKindsFixedMiddle,
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
              TranscribeTargetKinds.CircleSelect,
            ],
          },
        },
        roll: {
          alignmentKinds: this.alignmentKindsFixedMiddle,
          transcription: {
            targets: [
              TranscribeTargetKinds.FreeText,
              TranscribeTargetKinds.Text,
            ],
          },
        },
        graph: {
          valueTypes: [
            "text",
            "calculate",
          ],
        },
      },
      userDefinitions: results[1],
      externalScripts: results[2],
      fontFamilyNames: results[3],
      designatedDoctorInformations: Enumerable.from(results[5])
        .distinct(g => g.typeCode)
        .select(g => ({
          code: g.typeCode,
          name: g.typeName
        }))
        .toArray(),
    };
  }
}
